import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";
import { api, Me } from "./api";
import FeatureRequestsBoard from "./FeatureRequestsBoard";
import RoadmapView from "./RoadmapView";
import ChangelogView from "./ChangelogView";
import RequestDetail from "./RequestDetail";
import TermsModal from "./TermsModal";

type Ctx = {
  me: Me | null;
  isStaff: boolean;
  termsAccepted: boolean;
  // Re-open the terms modal (after a 412 from the server, for instance).
  requestTerms: () => void;
};

const FeatureRequestsContext = createContext<Ctx>({ me: null, isStaff: false, termsAccepted: false, requestTerms: () => {} });
export const useFeatureRequests = () => useContext(FeatureRequestsContext);

/**
 * Top-level router for /feature-requests/*. Loads the caller's profile once
 * (staff flag + accepted terms version) and gates the whole section behind
 * the terms acknowledgement: the modal shows on entry until the current
 * version is accepted, and "Not now" leaves the section. The server's 412
 * on post/comment is the backstop.
 */
const FeatureRequestsRouter: React.FC = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState<Me | null>(null);
  const [forceTerms, setForceTerms] = useState(false);
  // 403 from /me: not a paying customer (trial or lapsed). The nav item is
  // already hidden for them; this covers a direct link.
  const [locked, setLocked] = useState(false);

  const load = useCallback(async () => {
    try {
      setMe(await api.me());
    } catch (error: any) {
      if (error?.response?.status === 403) {
        setLocked(true);
        return;
      }
      console.error("Error loading feature request profile:", error);
      toast.error("Failed to load feature requests");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const termsAccepted = Boolean(me && me.acceptedVersion === me.termsVersion);
  const termsOpen = Boolean(me) && (!termsAccepted || forceTerms);

  const accept = async () => {
    if (!me) return;
    try {
      await api.acceptTerms(me.termsVersion);
      setForceTerms(false);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Could not record your acceptance");
    }
  };

  if (locked) {
    return (
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-md mx-auto mt-16 rounded-lg border border-dashed border-base-300 bg-base-100 p-10 text-center">
          <LockClosedIcon className="size-10 mx-auto text-base-content/30 mb-3" />
          <p className="font-medium text-base-content">Feature Requests is for subscribers</p>
          <p className="text-sm text-base-content/60 mb-4">Once your subscription is active you can request features, vote, and follow the roadmap.</p>
          <Button variant="primary" onClick={() => navigate("/subscription")}>
            View plans
          </Button>
        </div>
      </div>
    );
  }

  return (
    <FeatureRequestsContext.Provider value={{ me, isStaff: Boolean(me?.isStaff), termsAccepted, requestTerms: () => setForceTerms(true) }}>
      <Routes>
        <Route index element={<FeatureRequestsBoard />} />
        <Route path="roadmap" element={<RoadmapView />} />
        <Route path="changelog" element={<ChangelogView />} />
        <Route path=":id" element={<RequestDetail />} />
      </Routes>
      <TermsModal open={termsOpen} onAccept={accept} onDecline={() => (forceTerms ? setForceTerms(false) : navigate("/cases"))} />
    </FeatureRequestsContext.Provider>
  );
};

export default FeatureRequestsRouter;
